"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, Clock, MoreVertical } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import LoadingSpinner from "@/components/loading-spinner";
import { toast } from "sonner";
import { ROLES, type RoleId } from "@/lib/constants/roles";

interface Show {
  id: number;
  venue_id: number;
  // "on_site" = a regular studio show; "on_road" = a road-show event.
  // Persisted in shows.show_type and returned by the shows API.
  show_type?: "on_site" | "on_road";
  duration_minutes: number;
  min_participants: number;
  max_participants: number;
  status_id?: number;
  article_code?: string | null;

  status?: string;
  created_at?: string;
  customer_types?: {
    id: number;
    name: string;
  };
  statuses?: {
    id: number;
    name: string;
  };

  show_infos?: Array<{
    id: number;
    name: string;
    subtitle?: string;
    desc?: string;
    lang: string;
  }>;
  show_details?: Array<{
    id: number;
    name: string;
    details: string;
    lang: string;
  }>;
  show_prices?: Array<{
    id?: number;
    price_type: string;
    amount: number;
    currency: string;
  }>;
}

interface CustomerType {
  id: number;
  name: string;
}

interface ShowsPageClientProps {
  venueId: number;
  venueName: string;
  customerTypes?: CustomerType[];
  userRoleId?: RoleId;
  // When true this manages road-show EVENTS (on_road shows): create tags
  // type='on_road', the list shows only on_road shows, customer type is hidden.
  roadShow?: boolean;
}

export function ShowsPageClient({
  venueId,
  venueName,
  customerTypes = [],
  userRoleId = ROLES.BOOKING_STAFF,
  roadShow = false,
}: ShowsPageClientProps) {
  // "event" on the road-show page, "show" on the normal shows page.
  const entityLabel = roadShow ? "event" : "show";
  const router = useRouter();

  // Determine the portal path based on user role
  const portalPath =
    userRoleId === ROLES.SUPER_ADMIN ? "/superadmin" : "/booking";
  const [showList, setShowList] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deletingShow, setDeletingShow] = useState<Show | null>(null);
  const [showName, setShowName] = useState("");
  const [isCreatingShow, setIsCreatingShow] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [editingShowId, setEditingShowId] = useState<number | null>(null);

  const [customerTypeId, setCustomerTypeId] = useState<number>(
    customerTypes[0]?.id || 1,
  );
  // Show type chosen in the Add modal. The road-show page forces on_road; the
  // normal Shows page lets the user pick (on_site is the default).
  const [showType, setShowType] = useState<"on_site" | "on_road">("on_site");

  const fetchShows = async () => {
    try {
      setLoading(true);
      const url = `/api/venues/${venueId}/shows`;
      const response = await fetch(url);

      if (!response.ok) {
        const text = await response.text();
        console.error("Shows fetch failed. Response:", text.substring(0, 200));
        throw new Error(`Failed to fetch shows: ${response.status}`);
      }

      const data = await response.json();
      setShowList(data.shows || []);
    } catch (error) {
      console.error("Error fetching shows:", error);
      toast.error("Failed to load shows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShows();
  }, [venueId]);

  const handleAddShow = async () => {
    if (!showName.trim()) return;

    try {
      setIsCreatingShow(true);

      const response = await fetch(`/api/venues/${venueId}/shows`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: showName,
          // The road-show page forces on_road; the normal Shows page uses the
          // type picked in the modal.
          type: roadShow ? "on_road" : showType,
          duration_minutes: 60,
          min_participants: 2,
          max_participants: 10,
          customer_type_id: customerTypeId,
          cut_off_days: 7, // Default 7 days cut-off for new shows
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create show");
      }

      const data = await response.json();
      console.log("Show creation response:", data);

      if (!data.show || !data.show.id) {
        throw new Error("Show was created but no ID was returned");
      }

      setShowName("");
      setIsCreatingShow(false);

      // Add the new show to the list immediately
      if (data.show) {
        setShowList((prevShows) => [data.show, ...prevShows]);
      }

      // Show redirecting state and navigate immediately
      setIsRedirecting(true);

      // Navigate immediately - dialog stays open until navigation completes.
      // The detail page derives the road-show warning from the show's
      // show_type, so no roadshow query param is needed.
      router.push(
        `${portalPath}/venue/${venueId}/shows/${data.show.id}?new=true`,
      );
    } catch (error) {
      console.error("Error creating show:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to create show",
      );
      setIsCreatingShow(false);
      setIsRedirecting(false);
    }
  };

  const handleDeleteShow = async () => {
    if (!deletingShow) return;

    try {
      const response = await fetch(
        `/api/venues/${venueId}/shows/${deletingShow.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete show");
      }

      toast.success("Show deleted successfully");
      await fetchShows();
    } catch (error) {
      console.error("Error deleting show:", error);
      toast.error("Failed to delete show");
    } finally {
      setDeletingShow(null);
    }
  };

  const handleEditShow = (show: Show) => {
    setEditingShowId(show.id);
    // Both normal and road shows open the standard show-detail page. The page
    // derives the road-show warning from the show's own show_type, so no query
    // param is needed.
    router.push(`${portalPath}/venue/${venueId}/shows/${show.id}`);
  };

  const getShowName = (show: Show) => {
    return (
      show.show_infos?.find((d) => d.lang === "en")?.name ||
      show.show_infos?.[0]?.name ||
      "Unnamed Show"
    );
  };

  const getShowSubtitle = (show: Show) => {
    return show.show_infos?.find((d) => d.lang === "en")?.subtitle || "";
  };

  const formatDuration = (minutes: number) => {
    return `${minutes} min`;
  };

  const formatParticipants = (min: number, max: number) => {
    return `${min}-${max} people`;
  };

  // The Shows page is the single place to manage both on_site and road shows,
  // so it lists everything (with a Type column). The legacy road-show page, if
  // still reached, narrows to on_road only.
  const displayedShows = roadShow
    ? showList.filter((s) => s.show_type === "on_road")
    : showList;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              {roadShow ? "Road show events" : "Shows"}
            </h1>
            <p className="text-gray-600 mt-2">
              {roadShow
                ? `Manage road show events for ${venueName}`
                : `Manage shows for ${venueName}`}
            </p>
          </div>

          <Dialog
            open={isAddDialogOpen}
            onOpenChange={(open) => {
              setIsAddDialogOpen(open);
              if (!open) {
                setShowName("");
                setIsCreatingShow(false);
                setIsRedirecting(false);
                setEditingShowId(null);
                setCustomerTypeId(customerTypes[0]?.id || 1);
                setShowType("on_site");
              }
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add {entityLabel}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader className="pb-4">
                <DialogTitle className="text-xl font-semibold">
                  Add new {entityLabel}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Enter a name for the new {entityLabel}. You can configure
                  additional settings after creation.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Show name *
                  </Label>
                  <Input
                    id="name"
                    value={showName}
                    onChange={(e) => setShowName(e.target.value)}
                    placeholder="e.g., Mystery Mansion"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    You can add translations and other details in Advanced
                    Settings
                  </p>
                </div>

                {!roadShow && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="showType"
                      className="text-sm font-medium text-gray-700"
                    >
                      Type *
                    </Label>
                    <select
                      id="showType"
                      value={showType}
                      onChange={(e) =>
                        setShowType(e.target.value as "on_site" | "on_road")
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="on_site">On-site show</option>
                      <option value="on_road">Road show</option>
                    </select>
                    <p className="text-xs text-gray-500">
                      Road shows reuse this settings page; some on-site features
                      may not apply.
                    </p>
                  </div>
                )}

                {!roadShow && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="customerType"
                      className="text-sm font-medium text-gray-700"
                    >
                      Customer type *
                    </Label>
                    <select
                      id="customerType"
                      value={customerTypeId}
                      onChange={(e) =>
                        setCustomerTypeId(parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {customerTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name === "corporate" ? "Corporate" : "Private"}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">
                      Choose who can book this show
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddShow}
                  disabled={!showName.trim() || isCreatingShow || isRedirecting}
                >
                  {isRedirecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Redirecting...
                    </>
                  ) : isCreatingShow ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add {entityLabel}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Shows List */}
        {loading ? (
          <div className="w-full">
            <div className="rounded-lg shadow overflow-hidden">
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-900">
                    Loading...
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : displayedShows.length > 0 ? (
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg border shadow overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow className="border-b-2">
                    <TableHead className="text-gray-700 font-semibold w-1/4 px-6 py-4">
                      Show name
                    </TableHead>
                    {!roadShow && (
                      <TableHead className="text-gray-700 font-semibold w-[12.5%] px-6 py-4">
                        Type
                      </TableHead>
                    )}
                    {!roadShow && (
                      <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                        Customer type
                      </TableHead>
                    )}
                    {!roadShow && (
                      <TableHead className="text-gray-700 font-semibold w-1/12 px-6 py-4">
                        Duration
                      </TableHead>
                    )}
                    <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                      Participants
                    </TableHead>
                    <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                      Accounting article
                    </TableHead>
                    <TableHead className="text-gray-700 font-semibold w-1/12 px-6 py-4">
                      Status
                    </TableHead>
                    <TableHead className="text-right text-gray-700 font-semibold px-6 py-4">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedShows.map((show) => (
                    <TableRow key={show.id} className="hover:bg-gray-50">
                      <TableCell className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {getShowName(show)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {getShowSubtitle(show)}
                          </div>
                        </div>
                      </TableCell>
                      {!roadShow && (
                        <TableCell className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                              show.show_type === "on_road"
                                ? "bg-purple-100 text-purple-800 border-purple-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }`}
                          >
                            {show.show_type === "on_road"
                              ? "Road show"
                              : "On-site"}
                          </span>
                        </TableCell>
                      )}
                      {!roadShow && (
                        <TableCell className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                              show.customer_types?.name === "corporate"
                                ? "bg-blue-100 text-blue-800 border-blue-200"
                                : "bg-green-100 text-green-800 border-green-200"
                            }`}
                          >
                            {show.customer_types?.name === "corporate"
                              ? "Corporate"
                              : "Private"}
                          </span>
                        </TableCell>
                      )}
                      {!roadShow && (
                        <TableCell className="px-6 py-4">
                          <span className="text-gray-700">
                            {formatDuration(show.duration_minutes)}
                          </span>
                        </TableCell>
                      )}
                      <TableCell className="px-6 py-4">
                        <span className="text-gray-700">
                          {formatParticipants(
                            show.min_participants,
                            show.max_participants,
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {show.customer_types?.name === "corporate" ? (
                          show.article_code ? (
                            <Badge
                              variant="secondary"
                              className="bg-blue-100 text-blue-800 border-blue-200"
                            >
                              {show.article_code}
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              className="bg-amber-100 text-amber-800 border-amber-200"
                            >
                              Not mapped
                            </Badge>
                          )
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </TableCell>

                      <TableCell className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                            show.status === "active"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : show.status === "inactive"
                                ? "bg-red-100 text-red-800 border-red-200"
                                : "bg-yellow-100 text-yellow-800 border-yellow-200"
                          }`}
                        >
                          {show.status === "active"
                            ? "Active"
                            : show.status === "inactive"
                              ? "Inactive"
                              : "Draft"}
                        </span>
                      </TableCell>

                      <TableCell className="text-right px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleEditShow(show)}
                              disabled={editingShowId === show.id}
                            >
                              {editingShowId === show.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent mr-2" />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeletingShow(show)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg border shadow overflow-hidden py-12">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {roadShow ? "No events yet" : "No shows yet"}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {roadShow
                    ? `Get started by adding your first road show event to ${venueName}.`
                    : `Get started by adding your first show to ${venueName}. Shows are the experiences your customers will book.`}
                </p>
                <Button
                  onClick={() => setIsAddDialogOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first {entityLabel}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Loading Overlay */}
        {editingShowId && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto mb-4" />
              <p className="text-lg font-semibold text-white">Loading...</p>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!deletingShow}
          onOpenChange={(open) => !open && setDeletingShow(null)}
        >
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-xl font-semibold text-red-600">
                Delete show
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Are you sure you want to delete "
                {deletingShow && getShowName(deletingShow)}"? This action cannot
                be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Trash2 className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                      This will permanently delete:
                    </h3>
                    <ul className="mt-2 text-sm text-red-700 space-y-1">
                      <li>
                        • Show "{deletingShow && getShowName(deletingShow)}"
                      </li>
                      <li>• All pricing and time slot configurations</li>
                      <li>• All associated bookings and history</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setDeletingShow(null)}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteShow}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete show
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
